import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const DealEditModal = ({ isOpen, onClose, deal, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    clientName: '',
    dealValue: '',
    expectedCloseDate: '',
    stage: 'Lead'
  });

  const [errors, setErrors] = useState({});

  const stageOptions = [
    { value: 'Lead', label: 'Lead' },
    { value: 'Qualified', label: 'Qualified' },
    { value: 'Proposal', label: 'Proposal' },
    { value: 'Negotiation', label: 'Negotiation' },
    { value: 'Closed Won', label: 'Closed Won' },
    { value: 'Closed Lost', label: 'Closed Lost' }
  ];

  useEffect(() => {
    if (deal) {
      setFormData({
        clientName: deal?.clientName || '',
        dealValue: deal?.dealValue || '',
        expectedCloseDate: deal?.expectedCloseDate || '',
        stage: deal?.stage || 'Lead'
      });
      setErrors({});
    } else {
      setFormData({
        clientName: '',
        dealValue: '',
        expectedCloseDate: '',
        stage: 'Lead'
      });
      setErrors({});
    }
  }, [deal, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.clientName?.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (!formData?.dealValue || formData?.dealValue <= 0) {
      newErrors.dealValue = 'Deal value must be greater than 0';
    }

    if (!formData?.expectedCloseDate) {
      newErrors.expectedCloseDate = 'Expected close date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSave({
        ...deal,
        ...formData,
        dealValue: parseFloat(formData?.dealValue)
      });
      onClose();
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      onDelete(deal?.id);
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={deal ? 'Edit Deal' : 'Add New Deal'}
      footer={
        <>
          {deal && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              iconName="Trash2"
              iconPosition="left"
            >
              Delete
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSubmit}>
            {deal ? 'Save Changes' : 'Add Deal'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        <Input
          label="Client Name"
          type="text"
          placeholder="Enter client name"
          value={formData?.clientName}
          onChange={(e) => handleChange('clientName', e?.target?.value)}
          error={errors?.clientName}
          required
        />

        <Input
          label="Deal Value (USD)"
          type="number"
          placeholder="Enter deal value"
          value={formData?.dealValue}
          onChange={(e) => handleChange('dealValue', e?.target?.value)}
          error={errors?.dealValue}
          required
          min="0"
          step="0.01"
        />

        <Input
          label="Expected Close Date"
          type="date"
          value={formData?.expectedCloseDate}
          onChange={(e) => handleChange('expectedCloseDate', e?.target?.value)}
          error={errors?.expectedCloseDate}
          required
        />

        <Select
          label="Pipeline Stage"
          options={stageOptions}
          value={formData?.stage}
          onChange={(value) => handleChange('stage', value)}
          required
        />
      </form>
    </Modal>
  );
};

export default DealEditModal;